// src/pages/HelpSupport.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SiteFooter from '../components/common/SiteFooter';
import AdminNav from '../components/admin/AdminNav';
import api from '../utils/api';
import { getCurrentUser } from '../utils/auth';

// Category ids; their names and the FAQ text come from the locale files (help.categories, help.faq)
const CATEGORIES = ['groups', 'orders', 'supplier', 'account'];

// Category of each FAQ, by its index in help.faq
const FAQ_CATEGORY = [
  'account', 'account', 'account',
  'groups', 'groups', 'groups', 'groups',
  'supplier', 'supplier',
  'orders', 'orders', 'orders',
  'groups', 'supplier',
];

// Audience of FAQs by index in help.faq. Anything not listed is public.
// This is display filtering only; the API enforces permissions itself.
const FAQ_AUDIENCE = { 6: 'organizer', 12: 'organizer', 13: 'supplier' };

// Shown only once a real support address is configured (REACT_APP_SUPPORT_EMAIL)
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;

export default function HelpSupport() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(null);

  // Text is read in the current language, so search and display always match what the reader sees
  // Signed-in users keep their role-based navigation here, as on other authenticated pages
  const signedIn = !!localStorage.getItem('user');
  const me = getCurrentUser();
  // A user may be both a supplier admin and an organizer, so each audience is checked separately
  const isSupplierAdmin = me?.role === 'supplieradmin' || me?.role === 'superadmin';
  const [isOrganizer, setIsOrganizer] = useState(false);

  // Organizer permissions are per group; the server marks each group the viewer can manage in myPermissions
  useEffect(() => {
    if (!me) return undefined;
    let live = true;
    api.get('/api/groups')
      .then((res) => {
        if (live && Array.isArray(res.data)) setIsOrganizer(res.data.some((g) => (g.myPermissions || []).length > 0));
      })
      .catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const audienceAllowed = (i) => {
    const audience = FAQ_AUDIENCE[i];
    if (audience === 'organizer') return isOrganizer;
    if (audience === 'supplier') return isSupplierAdmin;
    return true;
  };

  const faqs = FAQ_CATEGORY.map((cat, i) => ({ id: i, category: cat, q: t(`help.faq.${i}.q`), a: t(`help.faq.${i}.a`) }))
    .filter((f) => audienceAllowed(f.id));

  const term = query.trim().toLowerCase();
  const visible = faqs.filter((f) =>
    (!category || f.category === category) &&
    (!term || f.q.toLowerCase().includes(term) || f.a.toLowerCase().includes(term))
  );

  return (
    <>
    {signedIn && <AdminNav />}
    <div className="max-w-4xl mx-auto bg-white shadow p-6 rounded-md">
      <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-2">{t('help.title')}</h1>
      <p className="text-gray-600 mb-6">{t('help.subtitle')}</p>

      <label htmlFor="help-search" className="sr-only">{t('help.searchLabel')}</label>
      <input
        id="help-search"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('help.searchPlaceholder')}
        className="border p-2 w-full rounded mb-6"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {CATEGORIES.map((name) => (
          <button
            key={name}
            type="button"
            aria-pressed={category === name}
            onClick={() => setCategory(category === name ? null : name)}
            className={`text-left border rounded-md p-4 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-green-600 ${category === name ? 'border-green-600 bg-green-50' : 'bg-white'}`}
          >
            <span className="font-semibold text-gray-800">{t(`help.categories.${name}`)}</span>
            <span className="block text-sm text-gray-500">
              {t('help.articles', { count: faqs.filter((f) => f.category === name).length })}
            </span>
          </button>
        ))}
      </div>

      <h2 className="text-xl font-semibold text-gray-800 mb-3">{t('help.faqHeading')}</h2>
      {visible.length === 0 ? (
        <p className="text-gray-600 mb-6">{t('help.noMatch')}</p>
      ) : (
        <ul className="divide-y border rounded-md mb-8">
          {visible.map((f) => (
            <li key={f.id}>
              <details className="p-4">
                <summary className="cursor-pointer font-medium text-gray-800 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-600 rounded">
                  {f.q}
                </summary>
                <p className="mt-2 text-gray-600 break-words">{f.a}</p>
              </details>
            </li>
          ))}
        </ul>
      )}

      <section className="border-t pt-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('help.contactHeading')}</h2>
        <p className="text-gray-600 mb-2">
          {t('help.contactBody')}
        </p>
        {SUPPORT_EMAIL && (
          <p className="text-gray-600 break-words">
            {t('help.supportEmailLabel')} <a className="text-green-700 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        )}
      </section>
      <SiteFooter />
    </div>
    </>
  );
}
